// ============================================
// Diagnostico — cuestionario de piel (/diagnostico).
// Una pregunta por pantalla; al final el motor del backend arma la rutina
// (POST /store/rutina) y se muestra con RutinaResultado.
// Las preguntas vienen del backend: editarlas no requiere tocar este archivo.
// ============================================

import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import RutinaResultado from '../components/rutina/RutinaResultado';
import { useProducts } from '../hooks/useProducts';
import { getCuestionario, crearRutina, enviarRutina } from 'services/medusa/rutina';
import { useCustomer } from '../context/CustomerContext';
import './Diagnostico.css';

const GUARDADO = 'phys_diagnostico';
const MICROSCOPIO = '/images/secciones/diagnostico/microscopio.webp';

// "Recibe tu rutina en PDF" + cupón del 10%. Opcional: el resultado ya se ve sin dar el correo.
function EnviarRutina({ respuestas }) {
  const { customer } = useCustomer();
  const [email, setEmail] = useState(customer?.email || '');
  const [nombre, setNombre] = useState(customer?.first_name || '');
  const [estado, setEstado] = useState('');
  const [error, setError] = useState('');

  if (!respuestas) return null;
  if (estado === 'enviado') {
    return (
      <div className="dx-mail">
        <p className="dx-mail__title">Listo, revisa tu correo</p>
        <p className="dx-mail__text">Te mandamos tu rutina en PDF y tu código del 10% a {email}.</p>
      </div>
    );
  }

  const enviar = async (e) => {
    e.preventDefault();
    setEstado('enviando');
    setError('');
    try {
      await enviarRutina({ email, nombre, respuestas });
      setEstado('enviado');
    } catch (err) {
      setError(err.message || 'No pudimos enviar el correo. Intenta de nuevo.');
      setEstado('');
    }
  };

  return (
    <form className="dx-mail" onSubmit={enviar}>
      <p className="dx-mail__title">Recibe tu rutina en PDF y 10% de descuento</p>
      <p className="dx-mail__text">Te la mandamos al correo con un código para tu próxima compra.</p>
      <input className="dx-mail__input" type="text" placeholder="Tu nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="given-name" />
      <input className="dx-mail__input" type="email" placeholder="Tu correo" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
      {error && <p className="dx-error">{error}</p>}
      <button className="dx-btn" type="submit" disabled={estado === 'enviando'}>
        {estado === 'enviando' ? 'Enviando…' : 'Enviármela'}
      </button>
      <p className="dx-mail__legal">Un código por correo, de un solo uso y no acumulable con otros cupones.</p>
    </form>
  );
}

const leerGuardado = () => {
  try {
    return JSON.parse(localStorage.getItem(GUARDADO)) || null;
  } catch {
    return null;
  }
};

export default function Diagnostico() {
  const { allProducts } = useProducts();
  const [preguntas, setPreguntas] = useState([]);
  const [paso, setPaso] = useState(-1); // -1 = intro
  const [respuestas, setRespuestas] = useState({ secundarios: [] });
  const [verPorque, setVerPorque] = useState(false);
  const [resultado, setResultado] = useState(() => leerGuardado()?.resultado || null);
  const [respuestasEnviadas, setRespuestasEnviadas] = useState(() => leerGuardado()?.respuestas || null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCuestionario().then(setPreguntas).catch(() => setError('No pudimos cargar el diagnóstico. Intenta de nuevo.'));
  }, []);

  useEffect(() => {
    setVerPorque(false);
    window.scrollTo({ top: 0 });
  }, [paso, resultado]);

  const q = preguntas[paso];
  const total = preguntas.length;

  const enviar = async (r) => {
    setCargando(true);
    setError('');
    try {
      const res = await crearRutina({ respuestas: r });
      setResultado(res);
      setRespuestasEnviadas(r);
      try { localStorage.setItem(GUARDADO, JSON.stringify({ respuestas: r, resultado: res })); } catch {}
    } catch (e) {
      setError(e.message || 'No pudimos armar tu rutina. Intenta de nuevo.');
    } finally {
      setCargando(false);
    }
  };

  const siguiente = (r) => {
    if (paso < total - 1) setPaso(paso + 1);
    else enviar(r);
  };

  const elegir = (id) => {
    const r = { ...respuestas, [q.id]: id };
    setRespuestas(r);
    setTimeout(() => siguiente(r), 200);
  };

  const alternar = (id) => {
    const actual = respuestas[q.id] || [];
    const nuevo = actual.includes(id)
      ? actual.filter((x) => x !== id)
      : actual.length < (q.max || Infinity) ? [...actual, id] : actual;
    setRespuestas({ ...respuestas, [q.id]: nuevo });
  };

  const repetir = () => {
    try { localStorage.removeItem(GUARDADO); } catch {}
    setResultado(null);
    setRespuestas({ secundarios: [] });
    setPaso(0);
  };

  const seo = (
    <SEO
      title="Diagnóstico de piel"
      description="Responde 9 preguntas y recibe una rutina de mañana y noche hecha para tu piel, con productos Physiology."
      path="/diagnostico"
    />
  );

  // ── Resultado ──
  if (resultado) {
    const { diagnostico } = resultado;
    return (
      <main className="dx dx--result">
        {seo}
        <aside className="dx-summary">
          <p className="dx-eyebrow">Tu diagnóstico</p>
          <h1 className="dx-title">{diagnostico.etiqueta}</h1>
          <p className="dx-focus">Enfoque: {diagnostico.enfoque}</p>
          <p className="dx-text">{diagnostico.texto}</p>
          <button className="dx-link" onClick={repetir}>Repetir diagnóstico</button>
          <EnviarRutina respuestas={respuestasEnviadas} />
        </aside>
        <section className="dx-routine">
          <RutinaResultado rutina={resultado} productos={allProducts} carrito={resultado.productos} />
        </section>
      </main>
    );
  }

  // ── Intro ──
  if (paso === -1) {
    return (
      <main className="dx dx--intro">
        {seo}
        <img className="dx-intro__img" src={MICROSCOPIO} alt="Serum BHA 1.5% bajo el microscopio" />
        <div className="dx-card dx-card--intro">
          <p className="dx-eyebrow">Diagnóstico de piel</p>
          <h1 className="dx-title">Tu rutina, hecha para tu piel</h1>
          <p className="dx-text">
            Responde {total || 9} preguntas y te armamos una rutina de mañana y noche: qué usar, en qué orden y qué días.
          </p>
          <p className="dx-meta">Menos de 3 minutos</p>
          {error && <p className="dx-error">{error}</p>}
          <button className="dx-btn" disabled={!total} onClick={() => setPaso(0)}>Empezar</button>
        </div>
      </main>
    );
  }

  // ── Pregunta ──
  const excluidas = q.excluirDe ? [respuestas[q.excluirDe]] : [];
  const opciones = q.opciones.filter((o) => !excluidas.includes(o.id));
  const seleccion = respuestas[q.id];

  return (
    <main className="dx">
      {seo}
      <div className="dx-progress" aria-hidden="true">
        <span style={{ width: `${((paso + 1) / total) * 100}%` }} />
      </div>
      <div className="dx-card">
        <p className="dx-eyebrow">{paso + 1} / {total}</p>
        <h1 className="dx-question">{q.pregunta}</h1>
        {q.tipo === 'varias' && <p className="dx-meta">Elige hasta {q.max}</p>}

        <div className="dx-options">
          {opciones.map((o) => {
            const activo = q.tipo === 'varias' ? (seleccion || []).includes(o.id) : seleccion === o.id;
            return (
              <button
                key={o.id}
                className={`dx-option ${activo ? 'is-active' : ''}`}
                aria-pressed={activo}
                disabled={cargando}
                onClick={() => (q.tipo === 'varias' ? alternar(o.id) : elegir(o.id))}
              >
                {o.texto}
              </button>
            );
          })}
        </div>

        <button className="dx-why" onClick={() => setVerPorque(!verPorque)} aria-expanded={verPorque}>
          ¿Por qué esta pregunta?
        </button>
        {verPorque && <p className="dx-why__text">{q.porque}</p>}

        {error && <p className="dx-error">{error}</p>}

        <div className="dx-nav">
          <button className="dx-link" onClick={() => setPaso(paso - 1)} disabled={cargando}>Anterior</button>
          {q.tipo === 'varias' && (
            <button className="dx-btn" onClick={() => siguiente(respuestas)} disabled={cargando}>
              {(seleccion || []).length ? 'Continuar' : 'Ninguna, continuar'}
            </button>
          )}
          {cargando && <span className="dx-meta">Armando tu rutina…</span>}
        </div>
      </div>
    </main>
  );
}
